/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Component, Fragment } from 'react'
import { injectIntl } from 'react-intl'

import { EuiContextMenuItem, EuiIcon, EuiLoadingSpinner, withEuiTheme } from '@elastic/eui'
import type { WithEuiThemeProps } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { StackDeployment, AsyncRequestState, ProfileState } from '@modules/ui-types'
import { addToast } from '@modules/cui/Toasts'
import { parseError } from '@modules/cui/Alert'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'
import DeletionSurveyModal from '@modules/resources-shared-components/DeletionSurveyModal'

import { hasOngoingConfigurationChange } from '@/lib/stackDeployments/selectors/configurationChanges'
import { isAzurePlusUser, isIntegratedAzurePlusDeployment } from '@/lib/marketPlace'
import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals'

import { getConfigForKey } from '../../../../../store'
import DeleteDeploymentModal from '../DeleteDeployment/DeleteDeploymentModal'

import type { WrappedComponentProps } from 'react-intl'

export type StateProps = {
  shutdownStackDeploymentRequest: AsyncRequestState
  profile: NonNullable<ProfileState>
}

export type DispatchProps = {
  fetchDeployment: () => void
  stopAndHideDeployment: () => void
  resetShutdownStackDeployment: () => void
}

export type ConsumerProps = {
  deployment: StackDeployment
  onDelete?: () => void
}

type Props = StateProps & DispatchProps & ConsumerProps & WrappedComponentProps & WithEuiThemeProps

type State = {
  isConfirmModalOpen: boolean
  isDeletionSurveyModalOpen: boolean
  requestChainProgress: boolean
}

class HideDeploymentInUserconsole extends Component<Props, State> {
  mounted: boolean = false

  state: State = {
    isConfirmModalOpen: false,
    isDeletionSurveyModalOpen: false,
    requestChainProgress: false,
  }

  componentDidMount() {
    this.mounted = true
  }

  componentDidUpdate(prevProps: Props) {
    const { deployment, resetShutdownStackDeployment } = this.props
    const wasPending = hasOngoingConfigurationChange({ deployment: prevProps.deployment })
    const isPending = hasOngoingConfigurationChange({ deployment })

    // Whenever we change from not pending to pending we need to make sure the restart button
    // is no longer in the 'successfully saved deployment' state.
    if (!wasPending && isPending) {
      resetShutdownStackDeployment()
    }
  }

  componentWillUnmount() {
    const { resetShutdownStackDeployment } = this.props
    resetShutdownStackDeployment()
    this.mounted = false
  }

  render() {
    const { shutdownStackDeploymentRequest, profile, deployment, theme } = this.props
    const { requestChainProgress } = this.state
    const isBusy = requestChainProgress || shutdownStackDeploymentRequest.inProgress

    const azurePlusUser = isAzurePlusUser(profile)

    const azurePlusDeployment = isIntegratedAzurePlusDeployment(deployment)
    const deleteOnAzurePlus = azurePlusUser && azurePlusDeployment

    const deleteActionStyles = css({
      color: theme.euiTheme.colors.danger,
    })

    return (
      <Fragment>
        <PermissionsGate
          permissions={[
            {
              type: 'deployment',
              action: 'shutdown',
              id: deployment.id,
            },
          ]}
        >
          {({ hasPermissions }) => (
            <EuiContextMenuItem
              css={deleteActionStyles}
              data-test-id='deploymentDelete-Btn'
              icon={isBusy ? <EuiLoadingSpinner size='m' /> : <EuiIcon type='trash' />}
              disabled={isBusy || deleteOnAzurePlus || !hasPermissions}
              onClick={this.openConfirmModal}
              toolTipContent={
                deleteOnAzurePlus && (
                  <FormattedMessage
                    id='deployment-shut-down-and-hide-deployment.shut-down-deployment-azure'
                    defaultMessage='Go to the Azure console to delete this deployment.'
                  />
                )
              }
              toolTipProps={{ position: 'bottom' }}
            >
              <FormattedMessage
                id='deployment-shut-down-and-hide-deployment.shut-down-deployment'
                defaultMessage='Delete deployment'
              />
            </EuiContextMenuItem>
          )}
        </PermissionsGate>

        {this.renderConfirmModal()}
        {this.renderDeletionSurveyModal()}
      </Fragment>
    )
  }

  renderConfirmModal() {
    const { deployment } = this.props
    const { isConfirmModalOpen } = this.state

    if (!isConfirmModalOpen) {
      return null
    }

    return (
      <DeleteDeploymentModal
        deployment={deployment}
        closeConfirmModal={this.closeConfirmModal}
        onDeleteButtonConfirm={() =>
          this.showDeletionSurvey() ? this.openDeletionSurveyModal() : this.stopAndHide()
        }
      />
    )
  }

  renderDeletionSurveyModal() {
    const {
      deployment,
      intl: { formatMessage },
    } = this.props
    const { isDeletionSurveyModalOpen } = this.state

    if (!isDeletionSurveyModalOpen) {
      return null
    }

    const showDeletionSurvey = this.showDeletionSurvey()

    if (!showDeletionSurvey) {
      return null
    }

    return (
      <DeletionSurveyModal
        resourceType={formatMessage({
          id: 'deletion-survey-modal.hosted.singular',
          defaultMessage: 'hosted deployment',
        })}
        resourceTypeShort={formatMessage({
          id: 'deletion-survey-modal.hosted.short',
          defaultMessage: 'deployment',
        })}
        resourceTypePlural={formatMessage({
          id: 'deletion-survey-modal.hosted.plural',
          defaultMessage: 'hosted deployments',
        })}
        architectureType={formatMessage({
          id: 'deletion-survey-modal.hosted.architecture',
          defaultMessage: 'Hosted',
        })}
        resourceContext={{
          type: 'hosted',
          solution: deployment.settings?.solution_type,
          region: getRegionId({ deployment }),
        }}
        onClose={() => {
          this.stopAndHide()
          this.closeDeletionSurveyModal()
        }}
      />
    )
  }

  closeConfirmModal = () => {
    this.setState({ isConfirmModalOpen: false })
  }

  closeDeletionSurveyModal = () => {
    this.setState({ isDeletionSurveyModalOpen: false })
  }

  openConfirmModal = () => {
    this.setState({ isConfirmModalOpen: true })
  }

  openDeletionSurveyModal = () => {
    this.closeConfirmModal()
    this.setState({ isDeletionSurveyModalOpen: true })
  }

  // only affects SaaS userconsole users
  showDeletionSurvey(): boolean {
    const isUserConsole = getConfigForKey(`APP_NAME`) === `userconsole`

    if (!isUserConsole) {
      return false
    }

    return true
  }

  stopAndHide = () => {
    const { stopAndHideDeployment, fetchDeployment, onDelete } = this.props

    if (onDelete) {
      onDelete()
    }

    Promise.resolve()
      .then(() => this.setState({ requestChainProgress: true, isConfirmModalOpen: false }))
      .then(() => stopAndHideDeployment())
      .then(() => {
        if (!onDelete) {
          return fetchDeployment()
        }

        return null
      })
      .then(() => {
        if (this.mounted) {
          return this.setState({ requestChainProgress: false })
        }

        return null
      })
      .catch((error) => {
        if (this.mounted) {
          addToast({
            family: `toast-error`,
            color: `danger`,
            iconType: `broom`,
            title: (
              <FormattedMessage
                id='toasts.title-deleting-failed'
                defaultMessage='Deleting your deployment failed'
              />
            ),
            text: parseError(error),
          })
          return this.setState({ requestChainProgress: false })
        }

        return null
      })
  }
}

export default withEuiTheme(injectIntl(HideDeploymentInUserconsole))
